import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import CoverCard from "../components/CoverCard";

const API_PREFIX = "";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export default function ArtistPage() {
  const { artistSlug } = useParams();
  const [covers, setCovers] = useState([]);
  const [error, setError] = useState("");

  // audio state copied from your SwordfishTrombone pattern
  const audioRefs = useRef({});
  const [playingId, setPlayingId] = useState(null);
  const [progress, setProgress] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_PREFIX}/api/covers`);
        if (!res.ok) throw new Error(`Failed: ${res.status}`);
        const data = await res.json();
        setCovers(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setError("Could not load songs.");
      }
    })();
  }, []);

  const filtered = useMemo(
    () => covers.filter((c) => slugify(c.artist) === artistSlug),
    [covers, artistSlug]
  );

  const artistName = useMemo(() => filtered[0]?.artist || "Artist", [filtered]);

  const toNum = (v, fallback = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  };

  const stopAudio = (id) => {
    const a = audioRefs.current[id];
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    setProgress((p) => ({ ...p, [id]: 0 }));
  };

  const togglePlay = (id) => {
    const audio = audioRefs.current[id];
    if (!audio) return;

    if (playingId === id) {
      audio.pause();
      setPlayingId(null);
      return;
    }

    if (playingId != null) stopAudio(playingId);

    audio
      .play()
      .then(() => setPlayingId(id))
      .catch(console.error);
  };

  const onTimeUpdate = (id, e) => {
    const a = e.target;
    if (!a.duration) return;
    setProgress((p) => ({ ...p, [id]: (a.currentTime / a.duration) * 100 }));
  };

  const onEnded = (id) => {
    if (playingId === id) setPlayingId(null);
    setProgress((p) => ({ ...p, [id]: 0 }));
  };

  const handleRate = async (id, rating) => {
    try {
      const form = new FormData();
      form.append("id", id);
      form.append("rating", rating);
      const res = await fetch(`${API_PREFIX}/api/rate`, { method: "POST", body: form });
      if (!res.ok) throw new Error(`Vote failed: ${res.status}`);
      const updated = await res.json();
      setCovers((prev) => prev.map((c) => (c.id === id ? updated : c)));
    } catch (e) {
      console.error(e);
      alert("Voting failed.");
    }
  };

  return (
    <main className="main-content">
      <div style={{ marginTop: 18, display: "flex", justifyContent: "space-between", gap: 12 }}>
        <h2 style={{ margin: 0 }}>{artistName}</h2>
        <Link to="/artists" style={{ color: "#ffd700", opacity: 0.9 }}>
          ← All artists
        </Link>
      </div>

      {error && <p>{error}</p>}
      {!error && filtered.length === 0 && <p>No songs for this artist yet.</p>}

      <div className="covers-list" style={{ marginTop: 12 }}>
        {filtered.map((cover) => (
          <CoverCard
            key={cover.id}
            cover={cover}
            isPlaying={playingId === cover.id}
            progressPct={progress[cover.id] || 0}
            onTogglePlay={togglePlay}
            onRate={handleRate}
            audioRef={(el) => {
              if (el) audioRefs.current[cover.id] = el;
            }}
            onTimeUpdate={onTimeUpdate}
            onEnded={onEnded}
            formatDate={formatDate}
            toNum={toNum}
          />
        ))}
      </div>
    </main>
  );
}
